import React from 'react';

import {
  ActionIcon,
  Button,
  Collapse,
  Divider,
  Grid,
  Group,
  Input,
  LoadingOverlay,
  Paper,
  Text,
  TextInput,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import { IconExternalLink } from '@tabler/icons-react';
import GymTypeSelect from 'components/form-fields/auto-complete-fields/GymTypeSelect';
import RegionSelect from 'components/form-fields/auto-complete-fields/RegionSelect';
import TimeZoneSelect from 'components/form-fields/auto-complete-fields/TimeZoneSelect';
import CountryAutoComplete from 'components/form-fields/select-fields/CountrySelect';
import ImageUploader from 'components/ImageUploader';
import { useLocalization } from 'context/localization-context';
import useUpdateGym from 'hooks/gym/useUpdateGym';
import InputMask from 'react-input-mask';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Link } from 'react-router-dom';
import { GymRetrieve } from 'types';
import { Country, CountryEnum } from 'types/enums';
import { emailValidator, phoneValidator } from 'utils/form-validators';
import { formatPhone, phoneInputCountries } from 'utils/formatters';
import notificationHandler from 'utils/notifications';

import ConnectHighLevelButton from './ConnectHighLevelButton';

interface FormValues {
  gymId: string;
  title: string;
  gymType: string | null;
  address: {
    addressLines: string | null;
    locality: string | null;
    region: string | null;
    zipCode: string | null;
    country: Country;
  };
  phone: string | null;
  email: string | null;
  notificationEmail: string | null;
  website: string | null;
  timezone: string;
  permission?: boolean;
}

const GeneralCard = ({
  gymData,
  gymDataIsLoading,
  gymDataIsSuccess,
  isImpersonating = false,
  permission = true,
}: {
  gymData?: GymRetrieve;
  gymDataIsLoading: boolean;
  gymDataIsSuccess: boolean;
  isImpersonating?: boolean;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { successNotification, errorNotification } = notificationHandler();

  const updateMutation = useUpdateGym();
  const initialValues: FormValues = gymData
    ? gymData
    : {
        gymId: '',
        title: '',
        gymType: '',
        address: {
          addressLines: '',
          locality: '',
          region: '',
          zipCode: '',
          country: CountryEnum.USA,
        },
        phone: null,
        email: null,
        notificationEmail: null,
        website: null,
        timezone: '',
      };

  const form = useForm<FormValues>({
    initialValues,

    validate: {
      phone: value => (value ? phoneValidator(value) : null),
      email: value => (value ? emailValidator(value) : null),
      notificationEmail: value => (value ? emailValidator(value) : null),
      timezone: value => (value ? null : 'Timezone is required'),
    },

    transformValues: values => ({
      ...values,
      phone: formatPhone(values.phone),
      address: {
        ...values.address,
        zipCode: values.address.zipCode?.toUpperCase() || null,
      },
    }),
  });

  const { copy } = useLocalization(form.values.address.country);

  const [isEditable, { open, close }] = useDisclosure(false);

  const handleCancelChanges = (): void => {
    form.setValues({ ...gymData });
    close();
  };

  const handleSubmit = (formData: FormValues): void => {
    if (gymData) {
      const request = {
        ...gymData,
        ...formData,
        gymMailConfig: null,
        gymType: formData.gymType as GymRetrieve['gymType'],
        timezone: formData.timezone as GymRetrieve['timezone'],
      };

      updateMutation.mutate(request, {
        onSuccess: response => {
          successNotification('Gym settings update success', `${response.data.title} successfully updated.`);
          close();
        },
        onError: error => {
          errorNotification('Gym settings update failed', error.response?.data.message);
        },
      });
    }
  };

  const homeAddressValue = [
    form.values.address?.addressLines,
    form.values.address?.locality,
    form.values.address?.region,
    form.values.address?.zipCode,
  ]
    .filter(i => !!i)
    .join(', ');

  useShallowEffect(() => {
    if (gymData && gymDataIsSuccess) {
      form.setValues({ ...gymData });
    }
  }, [
    gymData?.gymId,
    gymData?.title,
    gymData?.gymType,
    gymData?.address,
    gymData?.phone,
    gymData?.email,
    gymData?.notificationEmail,
    gymData?.website,
    gymData?.timezone,
  ]);

  return (
    <Paper shadow='xl' p='sm'>
      <LoadingOverlay visible={gymDataIsLoading} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Group position='apart'>
          <Title order={4}>General</Title>

          <Group spacing='xs'>
            {isEditable ? (
              <>
                <Button color='red' compact onClick={handleCancelChanges} loading={updateMutation.isLoading}>
                  Cancel
                </Button>
                <Button
                  type='submit'
                  color='green'
                  compact
                  loading={updateMutation.isLoading}
                  data-cy='gymSettingsGeneralCardSaveButton'
                >
                  Save
                </Button>
              </>
            ) : (
              <Button
                compact
                onClick={open}
                disabled={!permission}
                loading={gymDataIsLoading || updateMutation.isLoading}
                data-cy='gymSettingsEditButton'
              >
                Edit
              </Button>
            )}
          </Group>
        </Group>

        <Divider mx='-sm' my='sm' />

        {gymData && (
          <Group position='center'>
            <ImageUploader gymProp={gymData} />
          </Group>
        )}

        <Divider mx='-sm' my='sm' />

        <Grid align='center' gutter={6}>
          <Grid.Col span={4}>
            <Text fs='italic' c='dimmed' size='sm' align='right'>
              Gym Name
            </Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <TextInput
              size='xs'
              disabled={!isEditable}
              {...form.getInputProps('title')}
              sx={{ flex: 1 }}
              data-cy='gymSettingsGymTitle'
            />
          </Grid.Col>

          <Grid.Col span={4}>
            <Text fs='italic' c='dimmed' size='sm' align='right'>
              Gym Type
            </Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <GymTypeSelect
              size='xs'
              disabled={!isEditable}
              {...form.getInputProps('gymType')}
              sx={{ flex: 1 }}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Collapse in={!isEditable}>
              <Grid align='center' gutter={6}>
                <Grid.Col span={4}>
                  <Text fs='italic' c='dimmed' size='sm' align='right'>
                    Home Address
                  </Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <TextInput
                    size='xs'
                    value={homeAddressValue}
                    disabled={!isEditable}
                    sx={{ flex: 1 }}
                    data-cy='gymSettingsHomeAddress'
                  />
                </Grid.Col>
              </Grid>
            </Collapse>
          </Grid.Col>

          <Collapse in={isEditable}>
            <Grid align='center' gutter={6}>
              <Grid.Col span={4}>
                <Text fs='italic' c='dimmed' size='sm' align='right'>
                  Address
                </Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <TextInput
                  size='xs'
                  placeholder='Enter Address'
                  sx={{ flex: 1 }}
                  {...form.getInputProps('address.addressLines')}
                  data-cy='gymSettingsAddress'
                />
              </Grid.Col>

              <Grid.Col span={4}>
                <Text fs='italic' c='dimmed' size='sm' align='right'>
                  City
                </Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <TextInput
                  size='xs'
                  placeholder='Enter City'
                  sx={{ flex: 1 }}
                  {...form.getInputProps('address.locality')}
                  data-cy='gymSettingsCity'
                />
              </Grid.Col>

              <Grid.Col span={4}>
                <Text fs='italic' c='dimmed' size='sm' align='right'>
                  {copy.address.regionLabel}
                </Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <RegionSelect
                  country={form.values.address.country}
                  size='xs'
                  label={null}
                  sx={{ flex: 1 }}
                  clearable
                  byCountryProps={{
                    [CountryEnum.USA]: { 'data-cy': 'gymSettingsState' },
                  }}
                  {...form.getInputProps('address.region')}
                />
              </Grid.Col>

              <Grid.Col span={4}>
                <Text fs='italic' c='dimmed' size='sm' align='right'>
                  {copy.address.zipLabel}
                </Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <Input
                  size='xs'
                  component={InputMask}
                  mask={copy.address.zipInputMask}
                  placeholder={copy.address.zipPlaceholder}
                  {...form.getInputProps('address.zipCode')}
                  data-cy='gymSettingsZipcode'
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <Text fs='italic' c='dimmed' size='sm' align='right'>
                  Country
                </Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <CountryAutoComplete
                  size='xs'
                  label={null}
                  disabled
                  sx={{ flex: 1 }}
                  value={form.values.address.country}
                />
              </Grid.Col>
            </Grid>
          </Collapse>
        </Grid>

        <Divider mx='-sm' my='md' />

        <Grid align='center' gutter={6}>
          <Grid.Col span={4}>
            <Text fs='italic' c='dimmed' size='sm' align='right'>
              Time Zone
            </Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <TimeZoneSelect
              size='xs'
              sx={{ flex: 1 }}
              disabled
              {...form.getInputProps('timezone')}
              data-cy='gymSettingsTimezone'
            />
          </Grid.Col>

          <Grid.Col span={4}>
            <Text fs='italic' c='dimmed' size='sm' align='right'>
              Website
            </Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <TextInput
              size='xs'
              sx={{ flex: 1 }}
              disabled={!isEditable}
              {...form.getInputProps('website')}
              rightSection={
                form.values.website ? (
                  <Tooltip label='Open the Link' withArrow position='right'>
                    <ActionIcon component={Link} color='gray' to={form.values.website} target='__blank'>
                      <IconExternalLink size={theme.fontSizes.md} />
                    </ActionIcon>
                  </Tooltip>
                ) : null
              }
            />
          </Grid.Col>

          <Grid.Col span={4}>
            <Text fs='italic' c='dimmed' size='sm' align='right'>
              Phone
            </Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <Input.Wrapper size='xs' error={form.getInputProps('phone').error} data-cy='gymSettingsPhone'>
              <PhoneInput
                defaultCountry={form.values.address.country.toLowerCase()}
                countries={phoneInputCountries}
                value={form.values.phone || ''}
                onChange={phone => form.setFieldValue('phone', phone)}
                placeholder='Enter Phone Number'
                disabled={!isEditable}
                inputStyle={{ width: '90%' }}
              />
            </Input.Wrapper>
          </Grid.Col>

          <Grid.Col span={4}>
            <Text fs='italic' c='dimmed' size='sm' align='right'>
              Gym Email
            </Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <TextInput
              type='email'
              size='xs'
              sx={{ flex: 1 }}
              disabled={!isEditable}
              {...form.getInputProps('email')}
              data-cy='gymSettingsEmail'
            />
          </Grid.Col>

          <Grid.Col span={4}>
            <Text fs='italic' c='dimmed' size='sm' align='right'>
              Notification Email
            </Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <TextInput
              size='xs'
              sx={{ flex: 1 }}
              disabled={!isEditable}
              {...form.getInputProps('notificationEmail')}
              data-cy='gymSettingsNotificationEmail'
            />
          </Grid.Col>
          <Grid.Col span={12} mt='md' mb='xs'>
            <Group position='right'>{isImpersonating && <ConnectHighLevelButton />}</Group>
          </Grid.Col>
        </Grid>
      </form>
    </Paper>
  );
};

export default GeneralCard;
